import type {ReactNode, JSX} from 'react'
import {clsx} from 'clsx'
import {env} from '@/services/env/client'
import type {
	Tersed,
	Block,
	AdvancedPageTemplateContentItem,
	GlobalConfigContentItem,
} from '@/services/kontent-ai/types'
import type {RichTextContentItem} from '@/products/flexible-web-toolkit/blocks/rich-text'
import {RichTextBlock} from '@/products/flexible-web-toolkit/blocks/rich-text'
import type {BlogHeadingContentItem} from '@/products/flexible-web-toolkit/blocks/blog-heading'
import {BlogHeadingBlock} from '@/products/flexible-web-toolkit/blocks/blog-heading'
import type {AnchorContentItem} from '@/products/flexible-web-toolkit/blocks/anchor'
import {AnchorBlock} from '@/products/flexible-web-toolkit/blocks/anchor'
import type {ArticleHeaderBlockContentItem} from '@/products/flexible-web-toolkit/blocks/article-header-block'
import {ArticleHeaderBlock} from '@/products/flexible-web-toolkit/blocks/article-header-block'
import type {ArticleListerBlockContentItem} from '@/products/flexible-web-toolkit/blocks/article-lister-block'
import {ArticleListerBlockBlock} from '@/products/flexible-web-toolkit/blocks/article-lister-block'
import type {ArticleListerContentItem} from '@/products/flexible-web-toolkit/blocks/article-lister'
import {ArticleListerBlock} from '@/products/flexible-web-toolkit/blocks/article-lister'
import type {CtaButtonContentItem} from '@/products/flexible-web-toolkit/blocks/cta-button'
import {CtaButtonBlock} from '@/products/flexible-web-toolkit/blocks/cta-button'
import type {ArticleListerBlockLargeContentItem} from '@/products/flexible-web-toolkit/blocks/article-lister-block-large'
import {ArticleListerBlockLarge} from '@/products/flexible-web-toolkit/blocks/article-lister-block-large'
import type {HeaderBlockCarouselContentItem} from '@/products/flexible-web-toolkit/blocks/header-block-carousel'
import {HeaderBlockCarouselBlock} from '@/products/flexible-web-toolkit/blocks/header-block-carousel'
import type {CardListerContentItem} from '@/products/flexible-web-toolkit/blocks/card-lister'
import {CardListerBlock} from '@/products/flexible-web-toolkit/blocks/card-lister'
import type {RowContentItem} from '@/products/flexible-web-toolkit/blocks/row'
import {RowBlock} from '@/products/flexible-web-toolkit/blocks/row'
import type {TagSingleContentItem} from '@/products/flexible-web-toolkit/blocks/tag-single'
import {TagSingleBlock} from '@/products/flexible-web-toolkit/blocks/tag-single'
import type {TagBlockContentItem} from '@/products/flexible-web-toolkit/blocks/tag-block'
import {TagBlockBlock} from '@/products/flexible-web-toolkit/blocks/tag-block'
import type {ShareBlockContentItem} from '@/products/flexible-web-toolkit/blocks/share-block'
import {ShareBlockBlock} from '@/products/flexible-web-toolkit/blocks/share-block'
import type {ColumnCardListerContentItem} from '@/products/flexible-web-toolkit/blocks/column-card-lister'
import {ColumnCardListerBlock} from '@/products/flexible-web-toolkit/blocks/column-card-lister'
import type {BenefitsModuleContentItem} from '@/products/flexible-web-toolkit/blocks/benefits-module'
import {BenefitsModuleBlock} from '@/products/flexible-web-toolkit/blocks/benefits-module'
import type {CTAButtonEmailContentItem} from '@/products/flexible-web-toolkit/blocks/cta-button-email'
import {CTAButtonEmailBlock} from '@/products/flexible-web-toolkit/blocks/cta-button-email'
import type {LargeBenefitsBlockContentItem} from '@/products/flexible-web-toolkit/blocks/large-benefits-block/large'
import {LargeBenefitsBlockBlock} from '@/products/flexible-web-toolkit/blocks/large-benefits-block/large'
import type {ArrowheadIframeContentItem} from '@/products/flexible-web-toolkit/blocks/arrowhead-iframe'
import {ArrowheadIframeBlock} from '@/products/flexible-web-toolkit/blocks/arrowhead-iframe'
import type {TrustcodeWidgetContentItem} from '@/products/seresto-trustcode'
import {TrustcodeWidgetBlock} from '@/products/seresto-trustcode'
import type {ScrollingImagesContentItem} from '@/products/flexible-web-toolkit/blocks/scrolling-images'
import {ScrollingImagesBlock} from '@/products/flexible-web-toolkit/blocks/scrolling-images'
import type {ShareBlockStaticContentItem} from '@/products/flexible-web-toolkit/blocks/share-block-static'
import {ShareBlockStaticBlock} from '@/products/flexible-web-toolkit/blocks/share-block-static'
import type {ModuleBlockContentItem} from '@/products/flexible-web-toolkit/blocks/module-block'
import {ModuleBlockBlock} from '@/products/flexible-web-toolkit/blocks/module-block'
import type {SmallBenefitsBlockContentItem} from '@/products/flexible-web-toolkit/blocks/small-benefits-block/small'
import {SmallBenefitsBlockBlock} from '@/products/flexible-web-toolkit/blocks/small-benefits-block/small'
import type {SmallBenefitsModuleContentItem} from '@/products/flexible-web-toolkit/blocks/small-benefits-module/small-module'
import {SmallBenefitsModuleBlock} from '@/products/flexible-web-toolkit/blocks/small-benefits-module/small-module'
import type {BrandCardContentItem} from '@/products/flexible-web-toolkit/blocks/brand-card'
import {BrandTableCardBlock} from '@/products/flexible-web-toolkit/blocks/brand-card'
import type {RichCardContentItem} from '@/products/flexible-web-toolkit/blocks/rich-card'
import {RichCardBlock} from '@/products/flexible-web-toolkit/blocks/rich-card'
import {
	BuyOnlineLinksBlock,
	type BuyOnlineLinksContentItem,
} from '@/products/flexible-web-toolkit/blocks/buy-online-links'
import {
	CardBlock,
	type CardContentItem,
} from '@/products/flexible-web-toolkit/blocks/card'
import {
	FullWidthCarouselBlock,
	type FullWidthCarouselContentItem,
} from '@/products/flexible-web-toolkit/blocks/carousel'
import {
	CarouselCTABlockBlock,
	type CarouselCtaBlockContentItem,
} from '@/products/flexible-web-toolkit/blocks/carousel-cta-block'
import {type ClickToBuyMultiProductContentItem} from '@/products/where-to-buy/click2cart/click-to-buy-multi-product/click-to-buy-multi-product'
import type {ClickToBuySingleProductContentItem} from '@/products/where-to-buy/click2cart/click-to-buy-single-product/click-to-buy-single-product'
import type {ClickToCartContentItem} from '@/products/where-to-buy/smartcommerce'
import type {MikmakButtonContentItem} from '@/products/where-to-buy/mikmak/mikmak-button'
import {ClickToBuyMultiProductBlock} from '@/products/where-to-buy/click2cart/click-to-buy-multi-product'
import {ClickToBuySingleProductBlock} from '@/products/where-to-buy/click2cart/click-to-buy-single-product'
import {ClickToCartBlock} from '@/products/where-to-buy/smartcommerce'
import {MikMakButtonBlock} from '@/products/where-to-buy/mikmak/mikmak-button'
import type {CouponContentItem} from '@/products/your-pet-and-you/blocks/coupon'
import {CouponBlock} from '@/products/your-pet-and-you/blocks/coupon'
import type {ComparisonTableContentItem} from '@/products/flexible-web-toolkit/blocks/comparison-table'
import {ComparisonTableBlock} from '@/products/flexible-web-toolkit/blocks/comparison-table'
import type {ContentModuleWithBulletsContentItem} from '@/products/flexible-web-toolkit/blocks/with-bullets/content-module-with-bullets'
import {ContentModuleWithBulletsBlock} from '@/products/flexible-web-toolkit/blocks/with-bullets/content-module-with-bullets'
import type {ListerBlockWithBulletsContentItem} from '@/products/flexible-web-toolkit/blocks/with-bullets/lister-block-with-bullets'
import {ListerBlockWithBullets} from '@/products/flexible-web-toolkit/blocks/with-bullets/lister-block-with-bullets'
import type {CountrySelectorContentItem} from '@/products/country-selector'
import {CountrySelectorBlock} from '@/products/country-selector'
import type {CouponIframeContentItem} from '@/products/where-to-buy/revtrax/coupon-iframe'
import {CouponIframeBlock} from '@/products/where-to-buy/revtrax'
import type {DistributorCardContentItem} from '@/products/flexible-web-toolkit/blocks/distributor-card'
import {DistributorCardBlock} from '@/products/flexible-web-toolkit/blocks/distributor-card'
import type {DistributorCardListerContentItem} from '@/products/flexible-web-toolkit/blocks/distributor-card-lister'
import {DistributorCardListerBlock} from '@/products/flexible-web-toolkit/blocks/distributor-card-lister'
import type {ImageContentItem} from '@/products/flexible-web-toolkit/blocks/image'
import {ImageBlock} from '@/products/flexible-web-toolkit/blocks/image'
import type {EnhancedCardContentItem} from '@/products/flexible-web-toolkit/blocks/enhanced-card'
import {EnhancedCardBlock} from '@/products/flexible-web-toolkit/blocks/enhanced-card'
import type {BreadcrumbsContentItem} from '@/products/flexible-web-toolkit/blocks/breadcrumbs'
import {BreadcrumbsBlock} from '@/products/flexible-web-toolkit/blocks/breadcrumbs'
import type {IconListContentItem} from '@/products/flexible-web-toolkit/blocks/icon-list'
import {IconListBlock} from '@/products/flexible-web-toolkit/blocks/icon-list'
import type {FactListerBlockContentItem} from '@/products/flexible-web-toolkit/blocks/fact-lister-block'
import {FactListerBlock} from '@/products/flexible-web-toolkit/blocks/fact-lister-block'
import type {WistiaContentItem} from '@/products/flexible-web-toolkit/blocks/videos/wistia'
import {WistiaBlock} from '@/products/flexible-web-toolkit/blocks/videos/wistia'
import type {VideoBlockContentItem} from '@/products/flexible-web-toolkit/blocks/videos/video-block'
import {VideoBlockBlock} from '@/products/flexible-web-toolkit/blocks/videos/video-block'
import type {FindYourRepContentItem} from '@/products/flexible-web-toolkit/blocks/find-your-rep'
import {FindYourRepBlock} from '@/products/flexible-web-toolkit/blocks/find-your-rep'
import type {FaqContentItem} from '@/products/flexible-web-toolkit/blocks/faq-block'
import {FaqBlock} from '@/products/flexible-web-toolkit/blocks/faq-block'
import type {FullTabbedBlockContentItem} from '@/products/flexible-web-toolkit/blocks/full-tabbed-block'
import {FullTabbedBlockBlock} from '@/products/flexible-web-toolkit/blocks/full-tabbed-block'
import type {GovernanceBlockContentItem} from '@/products/flexible-web-toolkit/blocks/governance-block'
import {GovernanceBlockBlock} from '@/products/flexible-web-toolkit/blocks/governance-block'
import type {HeaderBlockWithCopyContentItem} from '@/products/flexible-web-toolkit/blocks/header-block-with-copy'
import {HeaderBlockWithCopyBlock} from '@/products/flexible-web-toolkit/blocks/header-block-with-copy'
import type {HistoryTimelineContentItem} from '@/products/flexible-web-toolkit/blocks/history-timeline'
import {HistoryTimelineBlock} from '@/products/flexible-web-toolkit/blocks/history-timeline'
import type {HomePageHeaderBlockContentItem} from '@/products/flexible-web-toolkit/blocks/home-page-header-block'
import {HomePageHeaderBlockBlock} from '@/products/flexible-web-toolkit/blocks/home-page-header-block'
import type {HomeVideoBannerContentItem} from '@/products/flexible-web-toolkit/blocks/home-video-banner'
import {HomeVideoBannerBlock} from '@/products/flexible-web-toolkit/blocks/home-video-banner'
import type {ImageGalleryContentItem} from '@/products/flexible-web-toolkit/blocks/image-gallery'
import {ImageGalleryBlock} from '@/products/flexible-web-toolkit/blocks/image-gallery'
import type {JotFormContentItem} from '@/products/campaign-forms/jotform'
import {JotFormBlock} from '@/products/campaign-forms/jotform'
import type {StepsContentItem} from '@/products/flexible-web-toolkit/blocks/steps'
import {StepsBlock} from '@/products/flexible-web-toolkit/blocks/steps'
import type {MediaCardContentItem} from '@/products/flexible-web-toolkit/blocks/media-card'
import {MediaCardBlock} from '@/products/flexible-web-toolkit/blocks/media-card'
import type {SmallTextContentItem} from '@/products/flexible-web-toolkit/blocks/small-text'
import {SmallTextBlock} from '@/products/flexible-web-toolkit/blocks/small-text'
import type {YoutubeVideoHeaderBlockContentItem} from '@/products/flexible-web-toolkit/blocks/youtube-video-header-block'
import {YoutubeVideoHeaderBlockBlock} from '@/products/flexible-web-toolkit/blocks/youtube-video-header-block'
import type {LinkContentItem} from '@/products/flexible-web-toolkit/blocks/link'
import {LinkBlock} from '@/products/flexible-web-toolkit/blocks/link'
import type {SectionHeaderBlockContentItem} from '@/products/flexible-web-toolkit/blocks/section-header-block'
import {SectionHeaderBlock} from '@/products/flexible-web-toolkit/blocks/section-header-block'
import type {KeyInfoModuleColumnsContentItem} from '@/products/flexible-web-toolkit/blocks/key-info-module-columns'
import {KeyInfoModuleColumnsBlock} from '@/products/flexible-web-toolkit/blocks/key-info-module-columns'
import type {TableContentItem} from '@/products/flexible-web-toolkit/blocks/table'
import {TableBlock} from '@/products/flexible-web-toolkit/blocks/table'
import type {RichTextWithCTAContentItem} from '@/products/flexible-web-toolkit/blocks/rich-text-with-cta'
import {RichTextWithCtaBlock} from '@/products/flexible-web-toolkit/blocks/rich-text-with-cta'
import type {KeyInfoModuleContentItem} from '@/products/flexible-web-toolkit/blocks/key-info-module'
import {KeyInfoModuleBlock} from '@/products/flexible-web-toolkit/blocks/key-info-module'
import type {MsFormsContentItem} from '@/products/flexible-web-toolkit/blocks/ms-forms'
import {MsFormsBlock} from '@/products/flexible-web-toolkit/blocks/ms-forms'
import type {OfficeLocationContentItem} from '@/products/flexible-web-toolkit/blocks/office-location'
import {OfficeLocationBlock} from '@/products/flexible-web-toolkit/blocks/office-location'
import type {ProductDirectoryCardContentItem} from '@/products/flexible-web-toolkit/blocks/product-directory-card'
import {ProductDirectoryCardBlock} from '@/products/flexible-web-toolkit/blocks/product-directory-card'
import type {PetNameFormContentItem} from '@/products/pet-name-finder/pet-name-form'
import {PetNameFormBlock} from '@/products/pet-name-finder/pet-name-form'
import type {PetViewAllNamesContentItem} from '@/products/pet-name-finder/pet-view-all-names'
import {PetViewAllNamesBlock} from '@/products/pet-name-finder/pet-view-all-names'
import type {ProductSectionModContentItem} from '@/products/flexible-web-toolkit/blocks/product-section-mod'
import {ProductSectionModBlock} from '@/products/flexible-web-toolkit/blocks/product-section-mod'
import type {PromoModuleContentItem} from '@/products/flexible-web-toolkit/blocks/promo-module'
import {PromoModuleBlock} from '@/products/flexible-web-toolkit/blocks/promo-module'
import type {PromotionCardGroupContentItem} from '@/products/flexible-web-toolkit/blocks/promotion-card-group'
import {PromotionCardGroupBlock} from '@/products/flexible-web-toolkit/blocks/promotion-card-group'
import type {QuizContentItem} from '@/products/flexible-web-toolkit/blocks/quiz'
import {QuizBlock} from '@/products/flexible-web-toolkit/blocks/quiz'
import type {QuoteContentItem} from '@/products/flexible-web-toolkit/blocks/quote'
import {QuoteBlock} from '@/products/flexible-web-toolkit/blocks/quote'
import type {QuoteSliderContentItem} from '@/products/flexible-web-toolkit/blocks/quote-slider'
import {QuoteSliderBlock} from '@/products/flexible-web-toolkit/blocks/quote-slider'
import type {ResourceCardContentItem} from '@/products/flexible-web-toolkit/blocks/resource-card'
import {ResourceCardBlock} from '@/products/flexible-web-toolkit/blocks/resource-card'
import type {ResponsiveLinkWithIconContentItem} from '@/products/flexible-web-toolkit/blocks/responsive-link-with-icon/responsive-link-with-icon'
import {ResponsiveLinkWithIconBlock} from '@/products/flexible-web-toolkit/blocks/responsive-link-with-icon/responsive-link-with-icon'
import type {PlayableWidgetContentItem} from '@/products/playable'
import {PlayableWidgetBlock} from '@/products/playable'
import type {StickyBarContentItem} from '@/products/flexible-web-toolkit/blocks/sticky-bar'
import {StickyBarBlock} from '@/products/flexible-web-toolkit/blocks/sticky-bar'
import type {RebateHistoryContentItem} from '@/products/dvm-rebates/rebate-history'
import {RebateHistoryBlock} from '@/products/dvm-rebates/rebate-history'
import type {SearchHeaderBlockContentItem} from '@/products/flexible-web-toolkit/blocks/search-header-block'
import {SearchHeaderBlockBlock} from '@/products/flexible-web-toolkit/blocks/search-header-block'
import type {SearchModuleContentItem} from '@/products/flexible-web-toolkit/blocks/search-module'
import {SearchModuleBlock} from '@/products/flexible-web-toolkit/blocks/search-module'
import type {ShowSearchResultsContentItem} from '@/products/flexible-web-toolkit/blocks/show-search-results'
import {ShowSearchResultsBlock} from '@/products/flexible-web-toolkit/blocks/show-search-results'
import type {SecondaryHeaderContentItem} from '@/products/flexible-web-toolkit/blocks/secondary-header'
import {SecondaryHeaderBlock} from '@/products/flexible-web-toolkit/blocks/secondary-header'
import type {SimpleContentBlockWithIconContentItem} from '@/products/flexible-web-toolkit/blocks/simple-content-block-with-icon'
import {SimpleContentBlockWithIconBlock} from '@/products/flexible-web-toolkit/blocks/simple-content-block-with-icon'
import type {RebatesTableAndFilterContentItem} from '@/products/your-pet-and-you/blocks/rebates-table-and-filter'
import {RebatesTableAndFilterBlock} from '@/products/your-pet-and-you/blocks/rebates-table-and-filter'
import type {RebatesBlockContentItem} from '@/products/your-pet-and-you/blocks/rebates-block'
import {RebatesBlockBlock} from '@/products/your-pet-and-you/blocks/rebates-block'
import type {SimpleListerBlockContentItem} from '@/products/flexible-web-toolkit/blocks/simple-lister-block'
import {SimpleListerBlockBlock} from '@/products/flexible-web-toolkit/blocks/simple-lister-block'
import type {ModuleDashboardPetCardsContentItem} from '@/products/your-pet-and-you/blocks/dashboard-page/module-dashboard-pet-cards'
import {ModuleDashboardPetCardsBlock} from '@/products/your-pet-and-you/blocks/dashboard-page/module-dashboard-pet-cards'
import type {HeadingActionsWrapperContentItem} from '@/products/your-pet-and-you/blocks/dashboard-page/heading-actions-wrapper'
import {HeadingActionsWrapperBlock} from '@/products/your-pet-and-you/blocks/dashboard-page/heading-actions-wrapper'
import type {HeroSectionContentItem} from '@/products/your-pet-and-you/blocks/dashboard-page/hero-section'
import {HeroSectionBlock} from '@/products/your-pet-and-you/blocks/dashboard-page/hero-section'
import type {ModuleMyRemindersContentItem} from '@/products/your-pet-and-you/blocks/dashboard-page/module-my-reminders'
import {ModuleMyRemindersBlock} from '@/products/your-pet-and-you/blocks/dashboard-page/module-my-reminders'
import type {ModuleMedicationReminderModalContentItem} from '@/products/your-pet-and-you/blocks/module-medication-reminder-modal'
import {ModuleMedicationReminderModalBlock} from '@/products/your-pet-and-you/blocks/module-medication-reminder-modal'
import type {PetCreationWizardContentItem} from '@/products/your-pet-and-you/blocks/pet-creation-wizard'
import {PetCreateWizardBlock} from '@/products/your-pet-and-you/blocks/pet-creation-wizard'
import type {ModuleTableLayoutContentItem} from '@/products/your-pet-and-you/blocks/dashboard-page/module-table-layout'
import {ModuleTableLayoutBlock} from '@/products/your-pet-and-you/blocks/dashboard-page/module-table-layout'
import type {ProductDirectoryContentItem} from '@/products/flexible-web-toolkit/blocks/product-directory'
import {ProductDirectoryBlock} from '@/products/flexible-web-toolkit/blocks/product-directory'
import type {TabbedBlockContentItem} from '@/products/flexible-web-toolkit/blocks/tabbed-block'
import {TabbedBlockBlock} from '@/products/flexible-web-toolkit/blocks/tabbed-block'
import type {TabbedContentBlockContentItem} from '@/products/flexible-web-toolkit/blocks/tabbed-content-block'
import {TabbedContentBlockBlock} from '@/products/flexible-web-toolkit/blocks/tabbed-content-block'
import type {VideoWithRichTextContentItem} from '@/products/flexible-web-toolkit/blocks/video-with-rich-text'
import {VideoWithRichTextBlock} from '@/products/flexible-web-toolkit/blocks/video-with-rich-text'
import type {ParasiteTrackerModuleContentItem} from '@/products/disease-map/parasite-tracker/parasite-tracker-module'
import {ParasiteTrackerModuleBlock} from '@/products/disease-map/parasite-tracker/parasite-tracker-module'
import type {FilterSideSelectorContentItem} from '@/products/flexible-web-toolkit/blocks/filter-side-selector'
import {FilterSideSelectorBlock} from '@/products/flexible-web-toolkit/blocks/filter-side-selector'
import type {FilterBlockContentItem} from '@/products/flexible-web-toolkit/blocks/filter-block'
import {FilterBlockBlock} from '@/products/flexible-web-toolkit/blocks/filter-block'
import type {TileContainerContentItem} from '@/products/flexible-web-toolkit/blocks/tile-container'
import {TileContainerBlock} from '@/products/flexible-web-toolkit/blocks/tile-container'
import type {TileWithIconContentItem} from '@/products/flexible-web-toolkit/blocks/tile-with-icon'
import {TileWithIconBlock} from '@/products/flexible-web-toolkit/blocks/tile-with-icon'
import type {ModuleCdcTabBlockContentItem} from '@/products/flexible-web-toolkit/blocks/module-cdc-tab-block'
import {ModuleCdcTabBlockBlock} from '@/products/flexible-web-toolkit/blocks/module-cdc-tab-block'
import type {ColumnContentItem} from '@/products/flexible-web-toolkit/blocks/Column'
import {ColumnBlock} from '@/products/flexible-web-toolkit/blocks/Column'
import type {ModuleCouponListerContentItem} from '@/products/flexible-web-toolkit/blocks/module-coupon-lister'
import {ModuleCouponListerBlock} from '@/products/flexible-web-toolkit/blocks/module-coupon-lister'
import type {FloatingComponentContentItem} from '@/products/flexible-web-toolkit/blocks/floating-component/floating-component'
import {FloatingComponentBlock} from '@/products/flexible-web-toolkit/blocks/floating-component/floating-component'
import type {ParasiteSelectorCtaCarouselContentItem} from '@/products/flexible-web-toolkit/blocks/parasite-selector-cta-carousel'
import {ParasiteSelectorCtaCarouselBlock} from '@/products/flexible-web-toolkit/blocks/parasite-selector-cta-carousel'
import type {InteractiveListContentItem} from '@/products/flexible-web-toolkit/blocks/interactive-list'
import {InteractiveListBlock} from '@/products/flexible-web-toolkit/blocks/interactive-list'
import type {TableBlockWithIconsContentItem} from '@/products/flexible-web-toolkit/blocks/table-block-with-icons'
import {TableBlockWithIconsBlock} from '@/products/flexible-web-toolkit/blocks/table-block-with-icons'
import type {PetDynamicArticleListerContentItem} from '@/products/flexible-web-toolkit/blocks/listers/dynamic-article-lister'
import {DynamicArticleListerBlock} from '@/products/flexible-web-toolkit/blocks/listers/dynamic-article-lister'
import type {FindABusinessContentItem} from '@/products/find-a-business'
import {FindABusinessBlock} from '@/products/find-a-business'
import type {FaqHeadingShareBlockContentItem} from '@/products/flexible-web-toolkit/blocks/faq-heading-share-block'
import {FaqHeadingShareBlockBlock} from '@/products/flexible-web-toolkit/blocks/faq-heading-share-block'
import {
	CdcScreenSetWrapperBlock,
	type CdcScreenSetWrapperContentItem,
} from '@/products/campaign-forms/cdc-screen-set-wrapper'
import type {FaqSearchHeaderContentItem} from '@/products/flexible-web-toolkit/blocks/faq-search-header'
import {FaqSearchHeaderBlock} from '@/products/flexible-web-toolkit/blocks/faq-search-header'
import {
	EnhancedProductDirectoryCardBlock,
	type EnhancedProductDirectoryCardContentItem,
} from '@/products/flexible-web-toolkit/blocks/enhanced-product-card'
import type {
	ListSectionContentItem,
	ProductPortfolioContentItem,
} from '@/products/flexible-web-toolkit/blocks/product-portfolio'
import {
	ListSectionBlock,
	ProductPortfolioBlock,
} from '@/products/flexible-web-toolkit/blocks/product-portfolio'
import {NadisWidgetBlock} from '@/products/nadis-widget'
import type {FaqSearchResultsContentItem} from '@/products/flexible-web-toolkit/blocks/faq-search-results'
import {FaqSearchResultsBlock} from '@/products/flexible-web-toolkit/blocks/faq-search-results'
import type {FeaturedArticleContentItem} from '@/products/flexible-web-toolkit/blocks/featured-article'
import {FeaturedArticleBlock} from '@/products/flexible-web-toolkit/blocks/featured-article'
import type {ModulePetProfileCardContentItem} from '@/products/your-pet-and-you/blocks/dashboard-page/module-pet-profile-card.mac.ts'
import {ModulePetProfileCardBlock} from '@/products/your-pet-and-you/blocks/dashboard-page/module-pet-profile-card.mac.ts'
import {
	AdoptWizardBlock,
	type AdoptWizardContentItem,
} from '@/products/your-pet-and-you/blocks/adoptwizard'
import type {FilterBarContentItem} from '@/products/flexible-web-toolkit/blocks/filter-bar'
import {FilterBarBlock} from '@/products/flexible-web-toolkit/blocks/filter-bar'
import type {CdcGigyaContentItem} from '@/products/auth/cdc-screensets/basic'
import {CdcGigyaBlock} from '@/products/auth/cdc-screensets/basic'
import type {CdcGigyaPopupContentItem} from '@/products/auth/cdc-screensets/popup'
import type {PDPPopupRebatesContentItem} from '@/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import {PDPPopupRebatesBlock} from '@/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import {CdcGigyaPopupBlock} from '@/products/auth/cdc-screensets/popup'
import type {ProductDirectoryListerContentItem} from '@/products/flexible-web-toolkit/blocks/listers/product-directory-lister'
import {ProductDirectoryListerBlock} from '@/products/flexible-web-toolkit/blocks/listers/product-directory-lister'
import type {FormSelectorContentItem} from '@/products/disease-map/form-selector'
import {FormSelectorBlock} from '@/products/disease-map/form-selector'
import {useAuth} from '@/products/auth/auth-provider'
import type {CurrentUserContext} from '@/models/current-user-context'
import type {VetRebateNoCrmIdFoundContentItem} from '@/products/dvm-rebates/vet-rebates/no-business-id-error'
import {VetRebateNoCrmIdFoundBlock} from '@/products/dvm-rebates/vet-rebates/no-business-id-error'
import type {VetRebatePermissionContentItem} from '@/products/dvm-rebates/vet-rebates/rebate-permission'
import {VetRebatePermissionBlock} from '@/products/dvm-rebates/vet-rebates/rebate-permission'
import type {ModuleArticleListerContentItem} from '@/products/flexible-web-toolkit/blocks/listers/module-article-lister'
import {ModuleArticleListerBlock} from '@/products/flexible-web-toolkit/blocks/listers/module-article-lister'
import type {ResourceCardListerContentItem} from '@/products/flexible-web-toolkit/blocks/listers/resource-card-lister'
import {ResourceCardListerBlock} from '@/products/flexible-web-toolkit/blocks/listers/resource-card-lister'
import type {MikmakInPageContentItem} from '@/products/where-to-buy/mikmak/mikmak-in-page'
import {MikMakInPageBlock} from '@/products/where-to-buy/mikmak/mikmak-in-page'
import type {MikmakWTBWidgetContentItem} from '@/products/where-to-buy/mikmak/mikmak-wtb/wtb-widget'
import {MikMakWTBWidgetBlock} from '@/products/where-to-buy/mikmak/mikmak-wtb/wtb-widget'
import type {BazaarVoiceReviewsContentItem} from '@/products/reviews/index'
import {BazaarVoiceReviewsBlock} from '@/products/reviews/index'
import type {BazaarVoiceRatingsContentItem} from '@/products/ratings'
import {BazaarVoiceRatingsBlock} from '@/products/ratings'
import {
	PearCommerceWidget,
	type PearCommerceWidgetContentItem,
} from '@/products/where-to-buy/pearcommerce/pear-shoppable-widget'
import {
	PearCommerceStoreLocator,
	type PearCommerceStoreLocatorContentItem,
} from '@/products/where-to-buy/pearcommerce/store-locator'
import {SkuProviderBlock} from '@/products/sku-provider/index'
import type {SkuProviderContentItem} from '@/products/sku-provider/index'
import {
	ImageComparisonBlock,
	type ImageComparisonContentItem,
} from '@/products/flexible-web-toolkit/blocks/image-comparison'
import {OutgrowWidgetBlock} from '@/products/outgrow'
import type {OutgrowWidgetContentItem} from '@/products/outgrow'
import type {PDPHeaderAccordionContentItem} from '@/products/pdp/pdp-accordion/pdp-accordion'
import {PDPHeaderAccordionBlock} from '@/products/pdp/pdp-accordion/pdp-accordion'
import type {PDPPopupWithRichTextContentItem} from '@/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import {PDPPopupWithRichTextBlock} from '@/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import type {PDPHeaderBlockContentItem} from '@/products/pdp/pdp-header/pdp-header-block'
import {PDPHeaderBlock} from '@/products/pdp/pdp-header/pdp-header-block'
import {SliderBlock} from '@/products/flexible-web-toolkit/blocks/slider'
import type {PDPSliderContentItem} from '@/products/flexible-web-toolkit/blocks/slider'
import type {
	CampaignFormContentItem,
	AdminCampaignFormContentItem,
} from '@/products/campaign-forms/forms-package/campaign-form'
import {
	CampaignFormBlock,
	AdminCampaignFormBlock,
} from '@/products/campaign-forms/forms-package/campaign-form'
import type {CountdownClockContentItem} from '@/products/countdown-clock'
import {CountdownClockBlock} from '@/products/countdown-clock'
import {
	CardModalTriggerBlock,
	type CardModalTriggerContentItem,
} from '@/products/flexible-web-toolkit/blocks/card-modal-trigger'

// **Please keep keys sorted alphabetically**
export interface BlockMap {
	adoptwizard: Block<AdoptWizardContentItem>
	anchor: Block<AnchorContentItem>
	arrowhead_iframe: Block<ArrowheadIframeContentItem>
	article_header_block: Block<ArticleHeaderBlockContentItem>
	article_lister_block_large: Block<ArticleListerBlockLargeContentItem>
	article_lister_block: Block<ArticleListerBlockContentItem>
	article_lister: Block<ArticleListerContentItem>
	bazaar_voice_ratings_widget: Block<BazaarVoiceRatingsContentItem>
	bazaar_voice_reviews_widget: Block<BazaarVoiceReviewsContentItem>
	benefits_module: Block<BenefitsModuleContentItem>
	better_page_model: Block<RichTextContentItem>
	blog_heading: Block<BlogHeadingContentItem>
	brand_card: Block<BrandCardContentItem>
	breadcrumbs: Block<BreadcrumbsContentItem>
	buy_online_links: Block<BuyOnlineLinksContentItem>
	admin_campaign_form: Block<AdminCampaignFormContentItem>
	campaign_form: Block<CampaignFormContentItem>
	card_lister: Block<CardListerContentItem>
	card_modal_trigger: Block<CardModalTriggerContentItem>
	card: Block<CardContentItem>
	carousel_cta_block: Block<CarouselCtaBlockContentItem>
	cdc_gigya_popup: Block<CdcGigyaPopupContentItem>
	cdc_gigya: Block<CdcGigyaContentItem>
	cdc_screen_set_wrapper: Block<CdcScreenSetWrapperContentItem>
	click_to_buy_multi_product: Block<ClickToBuyMultiProductContentItem>
	click_to_buy_single_product: Block<ClickToBuySingleProductContentItem>
	click_to_cart: Block<ClickToCartContentItem>
	column_card_lister: Block<ColumnCardListerContentItem>
	column: Block<ColumnContentItem>
	comparison_table: Block<ComparisonTableContentItem>
	content_module_with_bullets: Block<ContentModuleWithBulletsContentItem>
	country_selector: Block<CountrySelectorContentItem>
	coupon_iframe: Block<CouponIframeContentItem>
	coupon: Block<CouponContentItem>
	cta_button_email: Block<CTAButtonEmailContentItem>
	cta_button: Block<CtaButtonContentItem>
	distributor_card_lister: Block<DistributorCardListerContentItem>
	distributor_card: Block<DistributorCardContentItem>
	dynamic_article_lister: Block<PetDynamicArticleListerContentItem>
	enhanced_card: Block<EnhancedCardContentItem>
	enhanced_product_card: Block<EnhancedProductDirectoryCardContentItem>
	enhanced_product_directory_card: Block<EnhancedProductDirectoryCardContentItem>
	fact_lister_block: Block<FactListerBlockContentItem>
	faq_heading_share_block: Block<FaqHeadingShareBlockContentItem>
	faq_search_header: Block<FaqSearchHeaderContentItem>
	faq_search_results: Block<FaqSearchResultsContentItem>
	faq: Block<FaqContentItem>
	featured_article: Block<FeaturedArticleContentItem>
	filter_bar: Block<FilterBarContentItem>
	filter_block: Block<FilterBlockContentItem>
	filter_side_selector: Block<FilterSideSelectorContentItem>
	find_a_business: Block<FindABusinessContentItem>
	find_your_rep: Block<FindYourRepContentItem>
	floating_component: Block<FloatingComponentContentItem>
	form_selector: Block<FormSelectorContentItem>
	full_tabbed_block: Block<FullTabbedBlockContentItem>
	full_width_carousel: Block<FullWidthCarouselContentItem>
	governance_block: Block<GovernanceBlockContentItem>
	header_block_carousel: Block<HeaderBlockCarouselContentItem>
	header_block_with_copy: Block<HeaderBlockWithCopyContentItem>
	heading_actions_wrapper: Block<HeadingActionsWrapperContentItem>
	hero_section: Block<HeroSectionContentItem>
	history_timeline: Block<HistoryTimelineContentItem>
	home_page_header_block: Block<HomePageHeaderBlockContentItem>
	home_video_banner: Block<HomeVideoBannerContentItem>
	icon_list: Block<IconListContentItem>
	image_comparison: Block<ImageComparisonContentItem>
	image_gallery: Block<ImageGalleryContentItem>
	image: Block<ImageContentItem>
	interactive_list: Block<InteractiveListContentItem>
	jotform: Block<JotFormContentItem>
	key_info_module_columns: Block<KeyInfoModuleColumnsContentItem>
	key_info_module: Block<KeyInfoModuleContentItem>
	large_benefits_block: Block<LargeBenefitsBlockContentItem>
	link: Block<LinkContentItem>
	list_section: Block<ListSectionContentItem>
	lister_block_with_bullets: Block<ListerBlockWithBulletsContentItem>
	media_card: Block<MediaCardContentItem>
	mikmak_button: Block<MikmakButtonContentItem>
	mikmak_in_page: Block<MikmakInPageContentItem>
	mikmak_wtb_widget: Block<MikmakWTBWidgetContentItem>
	module_article_lister: Block<ModuleArticleListerContentItem>
	module_block: Block<ModuleBlockContentItem>
	module_cdc_tab_block: Block<ModuleCdcTabBlockContentItem>
	module_coupon_lister: Block<ModuleCouponListerContentItem>
	module_dashboard_pet_cards: Block<ModuleDashboardPetCardsContentItem>
	module_medication_reminder_modal: Block<ModuleMedicationReminderModalContentItem>
	module_my_reminders: Block<ModuleMyRemindersContentItem>
	module_pet_profile_card: Block<ModulePetProfileCardContentItem>
	module_table_layout: Block<ModuleTableLayoutContentItem>
	ms_forms: Block<MsFormsContentItem>
	nadis_widget: Block
	office_location: Block<OfficeLocationContentItem>
	outgrow_widget: Block<OutgrowWidgetContentItem>
	parasite_selector_cta_carousel: Block<ParasiteSelectorCtaCarouselContentItem>
	parasite_tracker_module: Block<ParasiteTrackerModuleContentItem>
	pdp_header_accordion: Block<PDPHeaderAccordionContentItem>
	pdp_header_block: Block<PDPHeaderBlockContentItem>
	pdp_popup_with_rich_text: Block<PDPPopupWithRichTextContentItem>
	pdp_popup_rebates: Block<PDPPopupRebatesContentItem>
	pdp_slider: Block<PDPSliderContentItem>
	pear_commerce_widget: Block<PearCommerceWidgetContentItem>
	pear_commerce_store_locator: Block<PearCommerceStoreLocatorContentItem>
	pet_creation_wizard: Block<PetCreationWizardContentItem>
	pet_name_form: Block<PetNameFormContentItem>
	pet_view_all_names: Block<PetViewAllNamesContentItem>
	playable_widget: Block<PlayableWidgetContentItem>
	product_directory_card: Block<ProductDirectoryCardContentItem>
	product_directory_lister: Block<ProductDirectoryListerContentItem>
	product_directory: Block<ProductDirectoryContentItem>
	product_portfolio: Block<ProductPortfolioContentItem>
	product_section_mod: Block<ProductSectionModContentItem>
	promo_module: Block<PromoModuleContentItem>
	promotion_card_group: Block<PromotionCardGroupContentItem>
	quiz: Block<QuizContentItem>
	quote_slider: Block<QuoteSliderContentItem>
	quote: Block<QuoteContentItem>
	rebate_history: Block<RebateHistoryContentItem>
	rebates_block: Block<RebatesBlockContentItem>
	rebates_table_and_filter: Block<RebatesTableAndFilterContentItem>
	resource_card: Block<ResourceCardContentItem>
	resource_card_lister: Block<ResourceCardListerContentItem>
	responsive_link_with_icon: Block<ResponsiveLinkWithIconContentItem>
	rich_card: Block<RichCardContentItem>
	rich_text_with_cta: Block<RichTextWithCTAContentItem>
	rich_text: Block<RichTextContentItem>
	row: Block<RowContentItem>
	scrolling_images: Block<ScrollingImagesContentItem>
	search_header_block: Block<SearchHeaderBlockContentItem>
	search_module: Block<SearchModuleContentItem>
	secondary_header: Block<SecondaryHeaderContentItem>
	section_header_block: Block<SectionHeaderBlockContentItem>
	share_block_static: Block<ShareBlockStaticContentItem>
	share_block: Block<ShareBlockContentItem>
	show_search_results: Block<ShowSearchResultsContentItem>
	simple_content_block_with_icon: Block<SimpleContentBlockWithIconContentItem>
	simple_lister_block: Block<SimpleListerBlockContentItem>
	sku_provider: Block<SkuProviderContentItem>
	small_benefits_block: Block<SmallBenefitsBlockContentItem>
	small_benefits_module: Block<SmallBenefitsModuleContentItem>
	small_text: Block<SmallTextContentItem>
	steps: Block<StepsContentItem>
	sticky_bar: Block<StickyBarContentItem>
	tabbed_block: Block<TabbedBlockContentItem>
	tabbed_content_block: Block<TabbedContentBlockContentItem>
	table_block_with_icons: Block<TableBlockWithIconsContentItem>
	table: Block<TableContentItem>
	tag_block: Block<TagBlockContentItem>
	tag_single: Block<TagSingleContentItem>
	tile_container: Block<TileContainerContentItem>
	tile_with_icon: Block<TileWithIconContentItem>
	trustcode_widget: Block<TrustcodeWidgetContentItem>
	vet_rebate_no_crm_id_found: Block<VetRebateNoCrmIdFoundContentItem>
	vet_rebate_permission: Block<VetRebatePermissionContentItem>
	video_block: Block<VideoBlockContentItem>
	video_with_rich_text: Block<VideoWithRichTextContentItem>
	wistia: Block<WistiaContentItem>
	youtube_video_header_block: Block<YoutubeVideoHeaderBlockContentItem>
	countdown_clock: Block<CountdownClockContentItem>
}

// **Please keep keys sorted alphabetically**
const map: Record<keyof BlockMap, BlockMap[keyof BlockMap]> = {
	adoptwizard: AdoptWizardBlock,
	anchor: AnchorBlock,
	arrowhead_iframe: ArrowheadIframeBlock,
	article_header_block: ArticleHeaderBlock,
	article_lister_block_large: ArticleListerBlockLarge,
	article_lister_block: ArticleListerBlockBlock,
	article_lister: ArticleListerBlock,
	bazaar_voice_ratings_widget: BazaarVoiceRatingsBlock,
	bazaar_voice_reviews_widget: BazaarVoiceReviewsBlock,
	benefits_module: BenefitsModuleBlock,
	better_page_model: RichTextBlock,
	blog_heading: BlogHeadingBlock,
	brand_card: BrandTableCardBlock,
	breadcrumbs: BreadcrumbsBlock,
	buy_online_links: BuyOnlineLinksBlock,
	admin_campaign_form: AdminCampaignFormBlock,
	campaign_form: CampaignFormBlock,
	card_lister: CardListerBlock,
	card_modal_trigger: CardModalTriggerBlock,
	card: CardBlock,
	carousel_cta_block: CarouselCTABlockBlock,
	cdc_gigya_popup: CdcGigyaPopupBlock,
	cdc_gigya: CdcGigyaBlock,
	cdc_screen_set_wrapper: CdcScreenSetWrapperBlock,
	click_to_buy_multi_product: ClickToBuyMultiProductBlock,
	click_to_buy_single_product: ClickToBuySingleProductBlock,
	click_to_cart: ClickToCartBlock,
	column_card_lister: ColumnCardListerBlock,
	column: ColumnBlock,
	comparison_table: ComparisonTableBlock,
	content_module_with_bullets: ContentModuleWithBulletsBlock,
	country_selector: CountrySelectorBlock,
	coupon_iframe: CouponIframeBlock,
	coupon: CouponBlock,
	cta_button_email: CTAButtonEmailBlock,
	cta_button: CtaButtonBlock,
	distributor_card_lister: DistributorCardListerBlock,
	distributor_card: DistributorCardBlock,
	dynamic_article_lister: DynamicArticleListerBlock,
	enhanced_card: EnhancedCardBlock,
	enhanced_product_card: EnhancedProductDirectoryCardBlock,
	enhanced_product_directory_card: EnhancedProductDirectoryCardBlock,
	fact_lister_block: FactListerBlock,
	faq_heading_share_block: FaqHeadingShareBlockBlock,
	faq_search_header: FaqSearchHeaderBlock,
	faq_search_results: FaqSearchResultsBlock,
	faq: FaqBlock,
	featured_article: FeaturedArticleBlock,
	filter_bar: FilterBarBlock,
	filter_block: FilterBlockBlock,
	filter_side_selector: FilterSideSelectorBlock,
	find_a_business: FindABusinessBlock,
	find_your_rep: FindYourRepBlock,
	floating_component: FloatingComponentBlock,
	form_selector: FormSelectorBlock,
	full_tabbed_block: FullTabbedBlockBlock,
	full_width_carousel: FullWidthCarouselBlock,
	governance_block: GovernanceBlockBlock,
	header_block_carousel: HeaderBlockCarouselBlock,
	header_block_with_copy: HeaderBlockWithCopyBlock,
	heading_actions_wrapper: HeadingActionsWrapperBlock,
	hero_section: HeroSectionBlock,
	history_timeline: HistoryTimelineBlock,
	home_page_header_block: HomePageHeaderBlockBlock,
	home_video_banner: HomeVideoBannerBlock,
	icon_list: IconListBlock,
	image_gallery: ImageGalleryBlock,
	image: ImageBlock,
	image_comparison: ImageComparisonBlock,
	interactive_list: InteractiveListBlock,
	jotform: JotFormBlock,
	key_info_module_columns: KeyInfoModuleColumnsBlock,
	key_info_module: KeyInfoModuleBlock,
	large_benefits_block: LargeBenefitsBlockBlock,
	link: LinkBlock,
	list_section: ListSectionBlock,
	lister_block_with_bullets: ListerBlockWithBullets,
	media_card: MediaCardBlock,
	mikmak_button: MikMakButtonBlock,
	mikmak_in_page: MikMakInPageBlock,
	mikmak_wtb_widget: MikMakWTBWidgetBlock,
	module_article_lister: ModuleArticleListerBlock,
	module_block: ModuleBlockBlock,
	module_cdc_tab_block: ModuleCdcTabBlockBlock,
	module_coupon_lister: ModuleCouponListerBlock,
	module_dashboard_pet_cards: ModuleDashboardPetCardsBlock,
	module_medication_reminder_modal: ModuleMedicationReminderModalBlock,
	module_my_reminders: ModuleMyRemindersBlock,
	module_pet_profile_card: ModulePetProfileCardBlock,
	module_table_layout: ModuleTableLayoutBlock,
	ms_forms: MsFormsBlock,
	nadis_widget: NadisWidgetBlock,
	office_location: OfficeLocationBlock,
	outgrow_widget: OutgrowWidgetBlock,
	parasite_selector_cta_carousel: ParasiteSelectorCtaCarouselBlock,
	parasite_tracker_module: ParasiteTrackerModuleBlock,
	pdp_header_accordion: PDPHeaderAccordionBlock,
	pdp_header_block: PDPHeaderBlock,
	pdp_popup_with_rich_text: PDPPopupWithRichTextBlock,
	pdp_popup_rebates: PDPPopupRebatesBlock,
	pdp_slider: SliderBlock,
	pear_commerce_widget: PearCommerceWidget,
	pear_commerce_store_locator: PearCommerceStoreLocator,
	pet_creation_wizard: PetCreateWizardBlock,
	pet_name_form: PetNameFormBlock,
	pet_view_all_names: PetViewAllNamesBlock,
	playable_widget: PlayableWidgetBlock,
	product_directory_card: ProductDirectoryCardBlock,
	product_directory_lister: ProductDirectoryListerBlock,
	product_directory: ProductDirectoryBlock,
	product_portfolio: ProductPortfolioBlock,
	product_section_mod: ProductSectionModBlock,
	promo_module: PromoModuleBlock,
	promotion_card_group: PromotionCardGroupBlock,
	quiz: QuizBlock,
	quote_slider: QuoteSliderBlock,
	quote: QuoteBlock,
	rebate_history: RebateHistoryBlock,
	rebates_block: RebatesBlockBlock,
	rebates_table_and_filter: RebatesTableAndFilterBlock,
	resource_card: ResourceCardBlock,
	resource_card_lister: ResourceCardListerBlock,
	responsive_link_with_icon: ResponsiveLinkWithIconBlock,
	rich_card: RichCardBlock,
	rich_text_with_cta: RichTextWithCtaBlock,
	rich_text: RichTextBlock,
	row: RowBlock,
	scrolling_images: ScrollingImagesBlock,
	search_header_block: SearchHeaderBlockBlock,
	search_module: SearchModuleBlock,
	secondary_header: SecondaryHeaderBlock,
	section_header_block: SectionHeaderBlock,
	share_block_static: ShareBlockStaticBlock,
	share_block: ShareBlockBlock,
	show_search_results: ShowSearchResultsBlock,
	simple_content_block_with_icon: SimpleContentBlockWithIconBlock,
	simple_lister_block: SimpleListerBlockBlock,
	sku_provider: SkuProviderBlock,
	small_benefits_block: SmallBenefitsBlockBlock,
	small_benefits_module: SmallBenefitsModuleBlock,
	small_text: SmallTextBlock,
	steps: StepsBlock,
	sticky_bar: StickyBarBlock,
	tabbed_block: TabbedBlockBlock,
	tabbed_content_block: TabbedContentBlockBlock,
	table_block_with_icons: TableBlockWithIconsBlock,
	table: TableBlock,
	tag_block: TagBlockBlock,
	tag_single: TagSingleBlock,
	tile_container: TileContainerBlock,
	tile_with_icon: TileWithIconBlock,
	trustcode_widget: TrustcodeWidgetBlock,
	vet_rebate_no_crm_id_found: VetRebateNoCrmIdFoundBlock,
	vet_rebate_permission: VetRebatePermissionBlock,
	video_block: VideoBlockBlock,
	video_with_rich_text: VideoWithRichTextBlock,
	wistia: WistiaBlock,
	youtube_video_header_block: YoutubeVideoHeaderBlockBlock,
	countdown_clock: CountdownClockBlock,
}

const blockMap = new Map<keyof BlockMap, Block>(
	Object.entries(map) as [keyof BlockMap, Block][]
)

function mapBlocks(
	blocks: Tersed[],
	page: Tersed<AdvancedPageTemplateContentItem>,
	globalConfig: Tersed<GlobalConfigContentItem>,
	className: string,
	isComponent: boolean,
	authState: CurrentUserContext
): ReactNode {
	const components = blocks.map<JSX.Element | null>((block) => {
		if (
			block.system.workflowStep === 'archived' ||
			!authState.checkUserRoleForBlock(block) ||
			!authState.isBlockVisible(block)
		)
			return null

		const {
			system: {type},
		} = block

		const key = type as keyof BlockMap
		const Component = blockMap.get(key)

		if (!Component) {
			if (env.NEXT_PUBLIC_NODE_ENV !== 'production') {
				return (
					<h2
						className="bg-red-500 p-4 text-center text-white"
						data-kontent-component-id={
							isComponent ? block.system.id : undefined
						}
						data-kontent-item-id={
							!isComponent ? block.system.id : undefined
						}
						key={block.system.id}
					>
						Unrecognized block - {block.system.type}
					</h2>
				)
			}

			return null
		}

		return (
			<div
				className={clsx(className, 'h-[inherit]')}
				data-kontent-component-id={
					isComponent ? block.system.id : undefined
				}
				data-kontent-item-id={
					!isComponent ? block.system.id : undefined
				}
				key={block.system.id}
			>
				<Component
					BlockMapper={BlockMapper}
					block={block}
					globalConfig={globalConfig}
					page={page}
				/>
			</div>
		)
	})

	return components
}

/**
 * The BlockMapper is used to take tersed data and map it into React components
 */
export function BlockMapper({
	blocks,
	page,
	globalConfig,
	className = '',
	isComponent = false,
}: {
	blocks: Tersed[]
	page: Tersed<AdvancedPageTemplateContentItem>
	globalConfig: Tersed<GlobalConfigContentItem>
	className?: string
	isComponent?: boolean
}): JSX.Element {
	const authState = useAuth()
	const components = mapBlocks(
		blocks,
		page,
		globalConfig,
		className,
		isComponent,
		authState
	)

	return <>{components}</>
}
